import { ComparatorContext } from '@kizi/comparator-core';
import { Div, Input } from '@kizi/core-components';
import { IconInputProps, IconV2 } from '@kizi/shared-components';
import nl from 'date-fns/locale/nl';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import DatePicker, { registerLocale, setDefaultLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useFieldTracking } from '../../../../utils/hooks';
import { withFormProps, WithFormProps } from '../../../HOCs';
import { DatePickerWrapper } from './DatePickerWrapper';

interface ReactDatePickerInputProps
  extends Omit<IconInputProps, 'iconProps' | 'size'> {
  dateFormat?: string;
  placeholder?: string;
  showTimeSelect?: boolean;
  showTimeSelectOnly?: boolean;
  timeIntervals?: number;
  timeCaption?: string;
  minDate?: Date;
  maxDate?: Date;
  excludeDates?: Date[];
  includeDates?: Date[];
  filterDate?: (date: Date) => boolean;
  showWeekNumbers?: boolean;
  showMonthDropdown?: boolean;
  showYearDropdown?: boolean;
  dropdownMode?: 'scroll' | 'select';
  yearDropdownItemNumber?: number;
  scrollableYearDropdown?: boolean;
  isClearable?: boolean;
  calendarStartDay?: number;
  locale?: any;
  peekNextMonth?: boolean;
  showMonthYearPicker?: boolean;
  showQuarterYearPicker?: boolean;
  showYearPicker?: boolean;
  inline?: boolean;
  fixedHeight?: boolean;
  monthsShown?: number;
  selectsStart?: boolean;
  selectsEnd?: boolean;
  startDate?: Date;
  endDate?: Date;
  excludeScrollbar?: boolean;
  customInput?: React.ReactElement;
  onCalendarClose?: () => void;
  onCalendarOpen?: () => void;
  calendarIcon?: React.ReactNode;
  showCalendar?: boolean;
  validators?: any;
  valueType?: string;
  meta?: {
    originalQuestion?: {
      valueType?: string;
      validators?: any[];
      [key: string]: any;
    };
    [key: string]: any;
  };
}

registerLocale('nl', nl);
setDefaultLocale('nl');

const ReactDatePickerInputBase: React.FC<
  WithFormProps<ReactDatePickerInputProps>
> = ({
  name,
  value,
  onChange,
  placeholder = 'DD-MM-YYYY',
  dateFormat = 'dd-MM-yyyy',
  showTimeSelect = false,
  showTimeSelectOnly = false,
  timeIntervals = 15,
  timeCaption = 'Time',
  minDate,
  maxDate,
  excludeDates,
  includeDates,
  filterDate,
  showWeekNumbers = false,
  showMonthDropdown = false,
  showYearDropdown = true,
  dropdownMode = 'scroll',
  yearDropdownItemNumber = 80,
  scrollableYearDropdown = true,
  isClearable = false,
  calendarStartDay = 1,
  locale = 'nl',
  peekNextMonth = true,
  showMonthYearPicker = false,
  showQuarterYearPicker = false,
  showYearPicker = false,
  inline = false,
  fixedHeight = false,
  monthsShown = 1,
  selectsStart = false,
  selectsEnd = false,
  startDate,
  endDate,
  excludeScrollbar = false,
  customInput,
  onCalendarClose,
  onCalendarOpen,
  disabled,
  readOnly,
  _error,
  _errorProps,
  _successProps,
  shouldTriggerAPICall,
  showCalendar = false,
  validators,
  valueType,
  meta,
  ...props
}) => {
  const { name: comparatorName } = useContext(ComparatorContext);
  const isOs = comparatorName.includes('os-');
  const { enhanceInputProps } = useFieldTracking();
  const inputRef = useRef<HTMLInputElement>(null);
  const datePickerRef = useRef<DatePicker>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isInputFocused, setIsInputFocused] = useState(false);

  const dateConstraints = useMemo(() => {
    let minDate: Date | null = null;
    let maxDate: Date | null = null;

    const validatorList = validators || meta?.originalQuestion?.validators;

    if (validatorList && Array.isArray(validatorList)) {
      validatorList.forEach((validator) => {

        if (validator.name === 'TodayOrGreater') {
          minDate = new Date();
        }

        if (validator.name === 'afterToday') {
          minDate = new Date();
        }

        if (validator.name === 'beforeToday') {
          maxDate = new Date();
        }

        // Check for future date limits
        if (
          validator.name === 'exceedsFuture' &&
          validator.args &&
          validator.args.length > 0
        ) {
          const daysInFuture = parseInt(validator.args[0]) || 90;
          const futureDate = new Date();
          futureDate.setDate(futureDate.getDate() + daysInFuture);
          maxDate = futureDate;
        }
      });
    }

    return { minDate, maxDate };
  }, [validators, meta]);

  const hasError = Boolean(_error);
  const hasSuccess = Boolean(!hasError && value && !disabled);

  useEffect(() => {
    if (value && typeof value === 'string') {
      const parts = value.split('-');
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const year = parseInt(parts[2], 10);

        if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
          const date = new Date(year, month, day);
          setSelectedDate(date);
        }
      }
    } else {
      setSelectedDate(null);
    }
  }, [value]);

  const formatDate = (date: Date): string => {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString();
    return `${day}-${month}-${year}`;
  };

  const handleInputFocus = () => {
    setIsInputFocused(true);
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsInputFocused(false);
    if (props?.onBlur) {
      props?.onBlur(e);
    }
  };

  const handleInputClick = () => {
    if (isCalendarOpen) {
      setIsCalendarOpen(false);
    }
  };

  const handleDateSelect = (date: Date | null) => {
    if (date) {
      const formattedDate = formatDate(date);

      const syntheticEvent = {
        target: { name: name || '', value: formattedDate },
        currentTarget: { name: name || '', value: formattedDate },
      } as React.ChangeEvent<HTMLInputElement>;

      onChange?.(syntheticEvent);

      setIsCalendarOpen(false);
    }
  };

  const handleCalendarIconClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!disabled && !readOnly) {
      setIsCalendarOpen(!isCalendarOpen);
    }
  };

  const handleCalendarClose = () => {
    setIsCalendarOpen(false);
    onCalendarClose?.();
  };

  const handleCalendarOpen = () => {
    setIsCalendarOpen(true);
    onCalendarOpen?.();
  };

  const enhancedProps = enhanceInputProps({ name, ...props });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        isCalendarOpen
      ) {
        setIsCalendarOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCalendarOpen]);

  return (
    <DatePickerWrapper hasError={hasError} hasSuccess={hasSuccess}>
      <div ref={containerRef} style={{ position: 'relative' }}>
        {/* Regular input for typing */}
        <Input
          {...enhancedProps}
          ref={inputRef}
          name={name}
          type="text"
          value={value || ''}
          onChange={onChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onClick={handleInputClick}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          data-testid={name ? `datepicker-${name}` : undefined}
          autoComplete="off"
          _error={_error}
          _errorProps={_errorProps}
          _successProps={_successProps}
          css={{
            width: 'calc(100% - 40px) !important',
            borderTopRightRadius: '0px !important',
            borderBottomRightRadius: '0px !important',
            borderRight: '0px !important',
            ...((isInputFocused || isCalendarOpen) && {
              border: '2px solid #007BFF !important',
              borderRight: '0px !important',
            }),
          }}
        />

        {/* Hidden DatePicker for calendar functionality */}
        {isCalendarOpen && (
          <div
            style={{
              position: 'absolute',
              top: '60px',
              left: 0,
              zIndex: 1000,
              width: '100%',
            }}
          >
            <DatePicker
              ref={datePickerRef}
              selected={selectedDate}
              onChange={handleDateSelect}
              onCalendarClose={handleCalendarClose}
              onCalendarOpen={handleCalendarOpen}
              dateFormat={dateFormat}
              showTimeSelect={showTimeSelect}
              showTimeSelectOnly={showTimeSelectOnly}
              timeIntervals={timeIntervals}
              timeCaption={timeCaption}
              minDate={dateConstraints.minDate || minDate}
              maxDate={dateConstraints.maxDate || maxDate}
              excludeDates={excludeDates}
              includeDates={includeDates}
              filterDate={filterDate}
              showWeekNumbers={showWeekNumbers}
              showMonthDropdown={showMonthDropdown}
              showYearDropdown={showYearDropdown}
              dropdownMode={dropdownMode}
              yearDropdownItemNumber={yearDropdownItemNumber}
              scrollableYearDropdown={scrollableYearDropdown}
              isClearable={isClearable}
              calendarStartDay={calendarStartDay}
              locale={locale}
              peekNextMonth={peekNextMonth}
              showMonthYearPicker={showMonthYearPicker}
              showQuarterYearPicker={showQuarterYearPicker}
              showYearPicker={showYearPicker}
              inline={true}
              fixedHeight={fixedHeight}
              monthsShown={monthsShown}
              selectsStart={selectsStart}
              selectsEnd={selectsEnd}
              startDate={startDate}
              endDate={endDate}
              excludeScrollbar={excludeScrollbar}
            />
          </div>
        )}

        {/* Clickable icon container */}
        <Div
          css={{
            position: 'absolute',
            right: 1,
            top: '50%',
            transform: 'translateY(-50%)',
            pointerEvents: 'auto',
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: disabled || readOnly ? 'default' : 'pointer',
            height: '100%',
            background: isCalendarOpen ? '#e8f0fe' : '#F0f0f0',
            width: '40px',
            borderTopRightRadius: isOs ? '8px' : '4px',
            borderBottomRightRadius: isOs ? '8px' : '4px',
            border: '1px solid #F0F0EF',
            borderColor: 'second.1',
            borderLeft: 0,
            boxSizing: 'border-box',
            ...((isInputFocused || isCalendarOpen) && {
              borderTop: '2px solid #007BFF',
              borderBottom: '2px solid #007BFF',
              borderRight: '2px solid #007BFF',
              borderLeft: 0,
            }),
          }}
          onClick={handleCalendarIconClick}
        >
          <IconV2 name="calendar" width={18} height={18} fill={'#9B9B9B'} />
        </Div>
      </div>
    </DatePickerWrapper>
  );
};

const ReactDatePickerInputWithFormProps = withFormProps(
  ReactDatePickerInputBase,
);

export const ReactDatePickerInput: React.FC<
  WithFormProps<ReactDatePickerInputProps>
> = (props) => (
  <ReactDatePickerInputWithFormProps
    {...props}
    _errorProps={{
      variant: 'error',
      _labelProps: { variant: 'error' },
      ...props._errorProps,
    }}
    _successProps={{
      variant: 'success',
      ...props._successProps,
    }}
  />
);

export type { ReactDatePickerInputProps };
